import { useState, useCallback, useRef } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import {
  buildUploadFailureResult,
  buildUploadCompletedFields,
  useKinnserUploadStatusPolling,
  type KinnserUploadResultFields,
} from './useKinnserUploadStatusPolling';
import { scheduleInKinnser } from '../../services/api';
import type {
  ScheduleInKinnserResponse,
  TaskCreateTabName,
} from '../../services/api';
import {
  getSurfaceableError,
  type SurfaceableResultFields,
} from '../../utils/apiErrorDetail';
import type {
  KinnserUploadJobSummary,
  KinnserUploadStatusResponse,
} from '../../services/api/types.gen';

export type KinnserSchedulingResult = (
  ScheduleInKinnserResponse | { success: false; message: string }
) &
  SurfaceableResultFields &
  KinnserUploadResultFields;

const GENERIC_SCHEDULING_FAILURE = 'Scheduling failed. Please try again.';

interface UseKinnserSchedulingOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  /** `intake.kinnser_upload_job`; resumes an in-flight upload poll after a reload. */
  kinnserUploadJob?: KinnserUploadJobSummary | null;
  taskName: string;
  tabName?: TaskCreateTabName | null;
  taskDate?: string | null;
  clinicianId: string | null;
}

interface UseKinnserSchedulingReturn {
  schedulingInKinnser: boolean;
  schedulingResult: KinnserSchedulingResult | null;
  handleScheduleInKinnser: () => Promise<void>;
}

/**
 * Hook for scheduling a visit (ROC or SOC) on an existing Kinnser episode.
 *
 * The backend encapsulates the multi-step flow (create task → upload → update details)
 * and records push_failed / Slack notification for 5xx/unexpected failures.
 * When the backend starts the upload asynchronously (`status: "processing"`),
 * the result is set only after the upload status poll finishes (EMR-3089).
 */
export function useKinnserScheduling({
  intakeId,
  intakeStatus,
  kinnserUploadJob,
  taskName,
  tabName = 'nursing',
  taskDate,
  clinicianId,
}: UseKinnserSchedulingOptions): UseKinnserSchedulingReturn {
  const [schedulingInKinnser, setSchedulingInKinnser] = useState(false);
  const [schedulingResult, setSchedulingResult] =
    useState<KinnserSchedulingResult | null>(null);
  // The POST response of an upload still processing; null after a reload.
  const processingResponseRef = useRef<ScheduleInKinnserResponse | null>(null);

  const clearSchedulingResult = useCallback(() => {
    setSchedulingResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearSchedulingResult
  );

  const handleUploadDone = useCallback(
    (status: KinnserUploadStatusResponse) => {
      const started = processingResponseRef.current;
      processingResponseRef.current = null;
      if (status.status !== 'completed') {
        setSchedulingResult(
          buildUploadFailureResult(status, GENERIC_SCHEDULING_FAILURE)
        );
        return;
      }
      setSchedulingResult({
        success: true,
        message: 'Scheduled in Kinnser successfully.',
        ...buildUploadCompletedFields(status, started),
        patient_task_key:
          status.job?.patient_task_key ?? started?.patient_task_key,
        task_name: status.job?.task_name ?? started?.task_name,
      });
    },
    []
  );

  const { pollingUpload, pollUploadStatus } = useKinnserUploadStatusPolling({
    intakeId,
    intakeStatus,
    flow: 'schedule',
    storedJob: kinnserUploadJob,
    onDone: handleUploadDone,
  });

  const handleScheduleInKinnser = useCallback(async () => {
    setSchedulingInKinnser(true);
    setSchedulingResult(null);

    try {
      if (!intakeId?.trim()) {
        setSchedulingResult({ success: false, message: 'No intake selected' });
        return;
      }
      const normalizedClinicianId = clinicianId?.trim();
      if (!normalizedClinicianId) {
        setSchedulingResult({
          success: false,
          message: 'Select a clinician before scheduling.',
        });
        return;
      }

      const response = await scheduleInKinnser({
        path: { intake_id: intakeId },
        body: {
          task_name: taskName,
          tab_name: tabName ?? undefined,
          task_date: taskDate ?? undefined,
          clinician_id: normalizedClinicianId,
        },
      });
      if (response.data?.status === 'processing') {
        // The task exists; the documents are still uploading. Keep the
        // button busy until the poll reports the outcome.
        processingResponseRef.current = response.data;
        await pollUploadStatus();
      } else if (response.data) {
        setSchedulingResult(response.data);
      } else {
        const surfaceable = getSurfaceableError(response);
        setSchedulingResult(
          surfaceable
            ? {
                success: false,
                message: surfaceable.message,
                surfaceable: true,
                error_code: surfaceable.errorCode,
              }
            : {
                success: false,
                message: GENERIC_SCHEDULING_FAILURE,
              }
        );
      }
    } catch (error) {
      console.error('Failed to schedule visit in Kinnser:', error);
      const surfaceable = getSurfaceableError(error);
      setSchedulingResult(
        surfaceable
          ? {
              success: false,
              message: surfaceable.message,
              surfaceable: true,
              error_code: surfaceable.errorCode,
            }
          : {
              success: false,
              message: 'An unexpected error occurred. Please try again.',
            }
      );
    } finally {
      setSchedulingInKinnser(false);
    }
  }, [intakeId, taskName, tabName, taskDate, clinicianId, pollUploadStatus]);

  return {
    schedulingInKinnser: schedulingInKinnser || pollingUpload,
    schedulingResult,
    handleScheduleInKinnser,
  };
}
